import { useState, useEffect, useCallback, useRef } from 'react';
import { VendorsListingService } from '../services/vendors.services';
import { useMessageHandler } from './useMessageHandler';
import { useStateRef } from './useStateRef';

interface Vendor {
  vendorId: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  status: "active" | "suspended";
  vendorCreatedAt: number;
  orderCount?: number;
  totalEarnings?: number;
  [key: string]: any;
}

interface UseVendorsParams {
  status?: string;
  search?: string;
  limit?: number;
}

export const useVendors = (params: UseVendorsParams = {}) => {
  const showMessage = useMessageHandler();
  
  const [vendors, , setVendors] = useStateRef<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const cursorRef = useRef<string | undefined>(undefined);
  const isFirstLoadRef = useRef(true);
  const isFetchingRef = useRef(false);
  const prevStatusRef = useRef<string | undefined>(params.status);
  const prevSearchRef = useRef<string | undefined>(params.search);
  const currentStatusRef = useRef<string | undefined>(params.status);

  // Update current refs every render
  currentStatusRef.current = params.status;

  // Effect to reset when filters change
  useEffect(() => {
    const statusChanged = prevStatusRef.current !== params.status;
    const searchChanged = prevSearchRef.current !== params.search;
    
    if (statusChanged || searchChanged) {
      console.log('[useVendors] Reset detected', {
        statusChanged,
        searchChanged,
        oldCursor: cursorRef.current ? 'present' : 'none',
      });
      
      // Clear everything
      cursorRef.current = undefined;
      isFirstLoadRef.current = true;
      prevStatusRef.current = params.status;
      prevSearchRef.current = params.search;
      
      // Clear vendors array
      setVendors([]);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.status, params.search]);

  const pageSize = params.limit || 10;
  
  const filteredVendors = vendors;
  
  const displayStartIndex = (currentPage - 1) * pageSize;
  const displayVendors = filteredVendors.slice(displayStartIndex, displayStartIndex + pageSize);
  const isDisplayFullSize = displayVendors.length === pageSize;
  
  // Calculate if there's a next page:
  // - If current page is not full, there's no next page
  // - If current page is full AND we have a cursor, there MIGHT be a next page
  // - If current page is full but we have no cursor, there's no next page
  const canHaveNextPage = isDisplayFullSize && !!cursorRef.current;
  
  // Check if the next page would be empty by looking at our current data
  const nextPageStartIndex = currentPage * pageSize;
  const hasDataForNextPage = filteredVendors.length > nextPageStartIndex;
  
  // Show "Next" button if:
  // 1. We have data for the next page already loaded, OR
  // 2. Current page is full AND we have a cursor (might have more data to fetch)
  const hasNext = hasDataForNextPage || canHaveNextPage;
  
  const needsMoreData = !isDisplayFullSize && !!cursorRef.current;

  /**
   * Fetch more vendors from backend with cursor
   */
  const fetchMore = useCallback(
    async () => {
      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        console.log('[useVendors] Fetch skipped - already fetching');
        return;
      }

      // Check if status changed since this function was queued
      if (currentStatusRef.current !== params.status) {
        console.log('[useVendors] Fetch aborted - status changed', {
          expectedStatus: params.status,
          currentStatus: currentStatusRef.current,
        });
        return;
      }

      // Read cursor directly from ref to get latest value
      const cursor = cursorRef.current;
      
      console.log('[useVendors] fetchMore called', {
        status: params.status,
        search: params.search,
        cursor: cursor ? 'present' : 'none',
      });

      isFetchingRef.current = true;
      setLoading(true);

      try {
        const response = await VendorsListingService.getVendors({
          filters: params.status ? { status: params.status as 'active' | 'suspended' } : undefined,
          searchTerm: params.search,
          limit: pageSize,
          lastEvaluatedKey: cursor,
        });

        // Double-check status hasn't changed while we were fetching
        if (currentStatusRef.current !== params.status) {
          console.log('[useVendors] Fetch result discarded - status changed during fetch');
          return;
        }

        if (response.success) {
          // If we got vendors, add them to the list
          if (response.vendors && response.vendors.length > 0) {
            setVendors(prev => [...prev, ...response.vendors]);
            cursorRef.current = response.pagination?.lastEvaluatedKey;
          } else {
            // If we got 0 vendors, clear the cursor
            // This will hide the "Next" button since there's no more data
            cursorRef.current = undefined;
          }
        } else {
          showMessage({ error: 'Failed to fetch vendors' });
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch vendors';
        showMessage({ error: errorMessage });
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [params.status, params.search, pageSize, setVendors, showMessage]
  );

  // Auto-fetch when needs more data OR when filters change
  // This matches the Orders page pattern exactly
  useEffect(() => {
    console.log('[useVendors] Auto-fetch effect triggered', {
      isFirstLoad: isFirstLoadRef.current,
      needsMoreData,
      isFetching: isFetchingRef.current,
      cursor: cursorRef.current ? 'present' : 'none',
      status: params.status,
    });
    
    // Auto-fetch on first load OR when we need more data to fill the current page
    // The isFetchingRef prevents concurrent fetches
    if (isFirstLoadRef.current || needsMoreData) {
      isFirstLoadRef.current = false;
      fetchMore();
    }
  }, [needsMoreData, fetchMore, params.status, params.search]);

  // Pagination info
  const curItemsCount = filteredVendors.length;
  const curPagesCount = Math.ceil(curItemsCount / pageSize);
  const curPageStartIndex = displayStartIndex + 1;
  const curPageEndIndex = displayStartIndex + displayVendors.length;

  return {
    vendors: displayVendors,
    allVendors: filteredVendors,
    loading: loading && displayVendors.length === 0,
    currentPage,
    setCurrentPage,
    hasMore: hasNext,
    totalPages: curPagesCount,
    totalItems: curItemsCount,
    pageStartIndex: curPageStartIndex,
    pageEndIndex: curPageEndIndex,
  };
};
